/**
 * Executive Home — Patch 36.0 · Project Obsidian Phase I.
 *
 * The unified executive operating experience. This is the *only* place
 * the founder should need to visit to answer:
 *
 *   1. What are today's priorities?     → Executive Briefing (Akinex)
 *   2. What can I continue right now?   → Immediate Actions
 *   3. Is anything on fire?             → Operational Health strip
 *   4. Which campaigns need me?         → Active Campaigns
 *   5. How is the business doing?       → Business Pulse
 *   6. What have we shipped?            → Performance Snapshot
 *   7. What happened recently?          → Recent Activity
 *
 * Ordering is spec-mandated (Patch 36.0 §2) and MUST NOT change. Every
 * panel is a deterministic renderer of canonical backend responses.
 *
 * Routing note: this component is the canonical founder landing page.
 * RootRedirect sends every authenticated visitor to ``/home`` which
 * mounts this component inside RootLayout. Do not add another home
 * variant — the nav label is "Executive Home", the URL is /home.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/entities/auth/store";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { greetingName } from "@/shared/lib/greeting";
import { useCurrentWorkspace } from "@/entities/workspace";
import { getSubscription } from "@/entities/billing";
import { useFirstLoginBrainRedirect } from "@/features/brain-mission";

import { ExecutiveBriefingCard } from "@/entities/akinex";
import { RecentActivityPanel } from "@/entities/activity";
import {
  ActiveCampaignsPanel,
  BrainSetupTile,
  BusinessPulsePanel,
  CampaignResumePanel,
  ImmediateActionsPanel,
  OperationalHealthStrip,
  PerformanceSnapshotPanel,
} from "@/features/executive-home";

export default function HomePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const me = useWorkspaceStore((s) => s.me);
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspaceId);
  const workspace = useCurrentWorkspace();
  const subscription = useQuery({
    queryKey: ["billing", "sub", workspace?.id ?? ""],
    queryFn: () => getSubscription(workspace!.id),
    enabled: !!workspace?.id,
    retry: false,
  });

  // Milestone 37.4 · Locked 4c first-login behaviour.
  // Automatically routes to /business-brain when the server-authoritative
  // gate reports no approved Business Brain AND the founder has not
  // explicitly deferred setup. Approved brain or a deferral flag → no
  // redirect. Public-preview / guest sessions never reach this mount.
  useFirstLoginBrainRedirect();

  // Greeting is owned by the Executive Briefing Card (Patch 35.5.1).
  // The workspace header immediately below the card is minimal by
  // design — no duplicate greeting, no duplicate summary.
  const firstName = useMemo(
    () =>
      greetingName({
        preferred_name: me?.preferred_name ?? user?.preferred_name,
        display_name: me?.display_name,
        name: user?.name,
      }),
    [me, user],
  );

  return (
    <div
      data-testid="executive-home"
      className="mx-auto max-w-[1120px] px-4 py-8 md:px-8 md:py-12 obsidian-fade-in"
    >
      <ExecutiveHomeHeader
        firstName={firstName}
        workspaceName={workspace?.profile.display_name ?? "your workspace"}
        planName={subscription.data?.plan.display_name ?? null}
        planLoading={subscription.isLoading}
      />

      {/* Section 1 · Executive Briefing (Akinex) — the founder's daily
          decision hub. Nothing may appear above it. */}
      <div
        className="mt-6 md:mt-8"
        data-testid="obsidian-section-briefing"
        // The card is workspace-aware internally; a workspace-scoped
        // React key ensures a fresh instance mounts on workspace
        // switch, guaranteeing no stale briefing bleeds through.
        key={`briefing-${currentWorkspaceId ?? "none"}`}
      >
        <div data-testid="akinex-briefing-slot">
          <ExecutiveBriefingCard />
        </div>
      </div>

      {/* Section 2 · Immediate Actions — "Continue Working". */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-immediate-actions"
           key={`imm-${currentWorkspaceId ?? "none"}`}>
        <ImmediateActionsPanel />
      </div>

      {/* Section 2.25 · Brain Setup Tile — Milestone 37.4.
          Persistent "Teach Orkexra Your Business" recommendation.
          Auto-hides once both brains are approved. */}
      <div className="mt-6 md:mt-8"
           data-testid="obsidian-section-brain-setup"
           key={`brain-setup-${currentWorkspaceId ?? "none"}`}>
        <BrainSetupTile />
      </div>

      {/* Section 2.5 · Campaign Resume Tile — Milestone 37.2.
          Auto-hides when no active Campaign mission exists. */}
      <div className="mt-6 md:mt-8"
           key={`campaign-resume-${currentWorkspaceId ?? "none"}`}>
        <CampaignResumePanel />
      </div>

      {/* Section 3 · Operational Health — is anything on fire? */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-operational-health"
           key={`ops-${currentWorkspaceId ?? "none"}`}>
        <OperationalHealthStrip />
      </div>

      {/* Section 4 · Active Campaigns — the ones needing me now. */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-active-campaigns"
           key={`cmp-${currentWorkspaceId ?? "none"}`}>
        <ActiveCampaignsPanel />
      </div>

      {/* Section 5 · Business Pulse — five-cell executive health. */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-business-pulse"
           key={`pulse-${currentWorkspaceId ?? "none"}`}>
        <BusinessPulsePanel />
      </div>

      {/* Section 6 · Performance Snapshot — numbers only. */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-performance"
           key={`perf-${currentWorkspaceId ?? "none"}`}>
        <PerformanceSnapshotPanel />
      </div>

      {/* Section 7 · Recent Activity — deterministic executive timeline. */}
      <div className="mt-6 md:mt-8" data-testid="obsidian-section-recent-activity"
           key={`activity-${currentWorkspaceId ?? "none"}`}>
        <RecentActivityPanel limit={10} />
      </div>
    </div>
  );
}

function ExecutiveHomeHeader({
  firstName,
  workspaceName,
  planName,
  planLoading,
}: {
  firstName: string;
  workspaceName: string;
  planName: string | null;
  planLoading: boolean;
}): JSX.Element {
  return (
    <header data-testid="executive-home-header">
      <p
        className="text-xs uppercase tracking-widest text-[var(--md-fog)]"
        data-testid="executive-home-eyebrow"
      >
        {workspaceName} · Executive Home
      </p>
      <p
        className="mt-1 text-[13px] text-[var(--md-fog)]"
        data-testid="executive-home-subtitle"
      >
        {firstName ? `Welcome back, ${firstName}. ` : ""}
        This is the state of your business today.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="executive-home-plan">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--md-fog)]">
          Current plan
        </span>
        <Link
          to="/billing"
          className="inline-flex min-h-9 items-center rounded-full border border-[var(--md-amber)]/50 bg-[var(--md-amber)]/10 px-3 py-1 text-xs font-semibold text-[var(--md-amber)] transition hover:border-[var(--md-amber)] hover:bg-[var(--md-amber)]/20"
          data-testid="executive-home-plan-link"
        >
          {planLoading ? "Loading…" : planName ?? "Plan unavailable"}
        </Link>
      </div>
    </header>
  );
}
